import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBack, MdChevronRight, MdCircle } from 'react-icons/md';
import deltaService from '../../services/deltaService';
import { Empty } from './DeltaUI';
import { SlideHead } from './DeckUI';

// The policy framework as a tree, and the work on it, in one place.
//
// THIS IS THE WORKSPACE; the plan is a document. The pack used to live inside Project Delta
// — the plan as presented to the Shareholder — while the queue of what needed doing lived
// outside it. Acting on a policy therefore meant going into the plan, coming out, and going
// back. They are one job: see the framework, find the gap, act on it. The plan keeps its
// governance slide as a VIEW; nothing actionable belongs in there.
//
// The hierarchy comes from `parent_policy` on the register (Charter Section 8.3 — no orphan
// policies), so this is the framework's own shape, not one invented for a screen.
//
// A node says whether anything has been DRAFTED, because "nothing here yet" is the most
// useful thing a governance framework can tell you about itself, and it is exactly what a
// flat list of titles hides.

const STAGE_CHIP = {
  draft: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
  in_review: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  awaiting_approval: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
};

export default function PoliciesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    deltaService.getPolicyWorkspace()
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-[11px]">
        <Link to="/" className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
          <MdArrowBack size={14} />
          All areas
        </Link>
      </div>

      <SlideHead headline="Policy Pack" />

      {data && (
        <div className="mb-5 flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-gray-500 dark:text-gray-400">
          <span>
            <strong className="text-gray-900 dark:text-gray-100 tabular-nums">
              {data.drafted} of {data.total}
            </strong>{' '}
            have a document
          </span>
          {data.awaiting_you > 0 && (
            <span className="font-medium text-brand-gold">
              {data.awaiting_you} waiting on you
            </span>
          )}
        </div>
      )}

      {loading && <Empty>Loading…</Empty>}
      {!loading && !data?.tree?.length && <Empty>The register is empty.</Empty>}

      <div className="space-y-1">
        {(data?.tree || []).map((n) => <Node key={n.reference} node={n} depth={0} />)}
      </div>

      <p className="mt-6 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
        The hierarchy is the register's own: every policy names the document it serves, so a
        policy with no parent is either misfiled or unnecessary (Charter Section 8.3).
      </p>
    </div>
  );
}

function Node({ node, depth }) {
  const dim = !node.has_document && !node.in_flight.length;
  return (
    <>
      <Link
        to={`/policies/${encodeURIComponent(node.reference)}`}
        style={{ paddingLeft: `${12 + depth * 18}px` }}
        className={`flex items-start gap-2 rounded-md border py-2.5 pr-3 transition-colors hover:border-brand-gold ${
          node.awaiting_you
            ? 'border-brand-gold/50 bg-brand-gold/[0.04]'
            : 'border-transparent hover:bg-black/[0.02] dark:hover:bg-white/[0.03]'}`}
      >
        {/* A filled dot means something exists to read; hollow means nothing has been
            drafted. The state of the framework, readable down the left edge. */}
        <MdCircle
          size={8}
          className={`mt-1.5 shrink-0 ${
            node.has_document ? 'text-emerald-500'
              : node.in_flight.length ? 'text-amber-500'
                : 'text-gray-300 dark:text-gray-700'}`}
        />

        <span className="min-w-0 flex-1">
          <span className={`block text-[13px] leading-snug ${
            dim ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {node.title}
          </span>

          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
            <span className="tabular-nums">{node.reference}</span>
            {node.in_flight.map((v) => (
              <span key={v.id} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STAGE_CHIP[v.stage] || ''}`}>
                v{v.label} · {v.stage_display}
              </span>
            ))}
            {!node.has_document && !node.in_flight.length && (
              <span className="italic">Nothing drafted — upload the first draft</span>
            )}
          </span>

          {node.next_step && (
            <span className="mt-1 block text-[11.5px] font-medium text-brand-gold">
              {node.next_step}
            </span>
          )}
        </span>

        <MdChevronRight size={16} className="mt-0.5 shrink-0 text-gray-300 dark:text-gray-600" />
      </Link>

      {node.children.map((c) => <Node key={c.reference} node={c} depth={depth + 1} />)}
    </>
  );
}
